import React from 'react'
import moment from 'moment'

import EventAvailableIcon from '@mui/icons-material/EventAvailable'

import Alert from '@mui/material/Alert'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import RosterAvailabilityBand from './RosterAvailabilityBand'
import RosterDutyCard from './RosterDutyCard'
import RosterLegend from './RosterLegend'
import RosterShiftCard from './RosterShiftCard'
import {
  describeAvailableStaff,
  getVisibleRosterStatusLegend,
  layoutRowAvailability,
  layoutRowShifts,
  layoutRowSpans,
  uncrewedDutyLegendItem,
} from './globalRosterUtils'
import { useNavigate } from 'react-router-dom'
import useIsMobile from '../../../../helpers/useIsMobile'

// Phones get a narrower sticky name column and tighter day columns so more
// than one day fits beside the names; the grid still scrolls sideways.
const gridColumns = (days, compact) =>
  compact
    ? `150px repeat(${days.length}, minmax(120px, 1fr))`
    : `220px repeat(${days.length}, minmax(132px, 1fr))`
const gridMinWidth = (days, compact) =>
  compact
    ? `calc(150px + ${days.length} * 120px)`
    : `calc(220px + ${days.length} * 132px)`
const todayColumnSx = {
  bgcolor: 'rgba(255, 193, 7, 0.14)',
  boxShadow:
    'inset 2px 0 0 rgba(245, 124, 0, 0.34), inset -2px 0 0 rgba(245, 124, 0, 0.34)',
}

const RosterGrid = ({
  days,
  groupedRows,
  selectedRoster,
  startDate,
  openNewShift,
  openExistingShift,
  getShiftApplications,
  showAvailableOnly = false,
  // Staff rows draw availability as bands; every other display mode gets a
  // per-day "Available staff" count row at the bottom instead
  displayMode = 'staff',
  availableStaffByDay = {},
  // PTS duties in range with no crew shifts - one row under Vacant Shifts
  uncrewedDuties = [],
}) => {
  const showAvailableStaffRow = displayMode !== 'staff'
  const visibleLegend = [
    ...getVisibleRosterStatusLegend(groupedRows, getShiftApplications),
    ...(uncrewedDuties.length ? [uncrewedDutyLegendItem] : []),
  ]

  const navigate = useNavigate()
  const compact = useIsMobile('sm')
  const todayKey = moment().format('YYYY-MM-DD')
  const isToday = (day) => day.format('YYYY-MM-DD') === todayKey

  const openDuty = (duty) =>
    navigate(
      `/pts/planning?date=${moment(duty.start).format('YYYYMMDD')}&duty=${duty._id}`,
    )

  // Sits directly under the Vacant Shifts row (or first when there is none)
  const renderUncrewedDutiesRow = (rowIndex) => {
    const { laidOutItems, laneCount } = layoutRowSpans(uncrewedDuties, days)
    return (
      <Box
        key="uncrewed-pts-duties"
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns(days, compact),
          gridAutoRows: 'min-content',
          minWidth: gridMinWidth(days, compact),
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: rowIndex % 2 ? 'grey.50' : 'background.paper',
        }}
      >
        <Box
          sx={{
            p: 1,
            gridColumn: '1',
            gridRow: `1 / ${laneCount + 1}`,
            borderRight: '1px solid',
            borderColor: 'divider',
            position: 'sticky',
            left: 0,
            zIndex: 2,
            bgcolor: rowIndex % 2 ? 'grey.50' : 'background.paper',
          }}
        >
          <Stack>
            <Typography variant="subtitle2" color="error.main">
              PTS duties without crew
            </Typography>
            <Typography variant="caption" color="text.secondary">
              No crew shifts yet · click to plan
            </Typography>
          </Stack>
        </Box>
        {days.map((day, dayIndex) => (
          <Box
            key={day.format('YYYY-MM-DD')}
            sx={{
              gridColumn: `${dayIndex + 2}`,
              gridRow: `1 / ${laneCount + 1}`,
              minHeight: 88,
              ...(isToday(day) ? todayColumnSx : {}),
            }}
          />
        ))}
        {laidOutItems.map(
          ({ item: duty, startIdx, endIdx, lane, continuesFromPrior, continuesToNext }) => (
            <Box
              key={duty._id}
              sx={{
                gridColumn: `${startIdx + 2} / ${endIdx + 3}`,
                gridRow: `${lane + 1}`,
                zIndex: 1,
                minWidth: 0,
                m: 0.75,
              }}
            >
              <RosterDutyCard
                duty={duty}
                continuesFromPrior={continuesFromPrior}
                continuesToNext={continuesToNext}
                visibleSpan={endIdx - startIdx + 1}
                onClick={() => openDuty(duty)}
              />
            </Box>
          ),
        )}
      </Box>
    )
  }
  const vacantRowIndex = groupedRows.findIndex((row) => row.id === 'vacant')
  const uncrewedRowPosition = vacantRowIndex === -1 ? 0 : vacantRowIndex + 1

  return (
    <Paper
      variant="outlined"
      sx={{ overflow: 'auto', maxHeight: '72vh', borderRadius: 2 }}
    >
      <RosterLegend
        items={visibleLegend}
        sx={{ minWidth: gridMinWidth(days, compact) }}
      />
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: gridColumns(days, compact),
          minWidth: gridMinWidth(days, compact),
          bgcolor: 'grey.50',
          borderBottom: '1px solid',
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 3,
        }}
      >
        <Box
          sx={{
            p: 1,
            fontWeight: 700,
            position: 'sticky',
            left: 0,
            zIndex: 4,
            bgcolor: 'grey.50',
            borderRight: '1px solid',
            borderColor: 'divider',
          }}
        >
          Rota Plan
        </Box>
        {days.map((day) => {
          const dayIsToday = isToday(day)

          return (
            <Box
              key={day.format('YYYY-MM-DD')}
              sx={{
                p: 1,
                fontWeight: 700,
                textAlign: 'center',
                ...(dayIsToday ? todayColumnSx : {}),
              }}
            >
              <Typography variant="subtitle2" fontWeight={800}>
                {day.format('ddd D')}
              </Typography>
              {dayIsToday && (
                <Typography
                  variant="caption"
                  color="warning.dark"
                  fontWeight={800}
                >
                  Today
                </Typography>
              )}
            </Box>
          )
        })}
      </Box>

      {groupedRows.length === 0 &&
      !showAvailableStaffRow &&
      uncrewedDuties.length === 0 ? (
        <Alert
          severity="info"
          sx={{ m: 2 }}
          action={
            <Button
              color="inherit"
              size="small"
              disabled={!selectedRoster}
              onClick={() => openNewShift(startDate)}
            >
              Add Shift
            </Button>
          }
        >
          {showAvailableOnly
            ? 'No staff have declared themselves available for this period. Turn off "Available staff only" to see everyone.'
            : selectedRoster
              ? 'No shifts found for this period.'
              : 'No shifts found for this period. To get started, create a rota plan with the New button in the control panel above — you can then add shifts to it.'}
        </Alert>
      ) : (
        [
          ...groupedRows.slice(0, uncrewedRowPosition),
          ...(uncrewedDuties.length ? [{ id: 'uncrewed-pts-duties', uncrewed: true }] : []),
          ...groupedRows.slice(uncrewedRowPosition),
        ].map((row, rowIndex) => {
          if (row.uncrewed) return renderUncrewedDutiesRow(rowIndex)

          // Shifts render once and span their day columns; same-day shifts
          // stack into lanes (extra grid rows) so spans never overlap. A
          // staff row's availability bands take the first lanes, shifts
          // start below them.
          const { laidOutBands, laneCount: bandLaneCount } =
            layoutRowAvailability(row.availability ?? [], days)
          const { laidOutShifts, laneCount } = layoutRowShifts(row.shifts, days)
          const totalLaneCount = bandLaneCount + laneCount

          return (
          <Box
            key={row.id}
            sx={{
              display: 'grid',
              gridTemplateColumns: gridColumns(days, compact),
              gridAutoRows: 'min-content',
              minWidth: gridMinWidth(days, compact),
              borderBottom: '1px solid',
              borderColor: 'divider',
              bgcolor: rowIndex % 2 ? 'grey.50' : 'background.paper',
            }}
          >
            <Box
              sx={{
                p: 1,
                gridColumn: '1',
                gridRow: `1 / ${totalLaneCount + 1}`,
                borderRight: '1px solid',
                borderColor: 'divider',
                position: 'sticky',
                left: 0,
                zIndex: 2,
                bgcolor: rowIndex % 2 ? 'grey.50' : 'background.paper',
              }}
            >
              {row.staff ? (
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  onClick={() =>
                    navigate('/employees/' + row.staff.userAccount._id)
                  }
                  sx={{ cursor: 'pointer' }}
                >
                  <Avatar
                    src={row.staff.userAccount?.photograph}
                    sx={{ width: 28, height: 28 }}
                  />
                  <Stack minWidth={0}>
                    <Typography variant="subtitle2" noWrap>
                      {row.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>
                      {row.subLabel}
                    </Typography>
                  </Stack>
                </Stack>
              ) : (
                <Stack>
                  <Typography
                    variant="subtitle2"
                    color={row.id === 'vacant' ? 'error.main' : 'text.primary'}
                  >
                    {row.label}
                  </Typography>
                  {row.subLabel && (
                    <Typography variant="caption" color="text.secondary">
                      {row.subLabel}
                    </Typography>
                  )}
                </Stack>
              )}
            </Box>

            {days.map((day, dayIndex) => (
              <Box
                key={day.format('YYYY-MM-DD')}
                sx={{
                  gridColumn: `${dayIndex + 2}`,
                  gridRow: `1 / ${totalLaneCount + 1}`,
                  minHeight: 88,
                  ...(isToday(day) ? todayColumnSx : {}),
                }}
              />
            ))}

            {laidOutBands.map(
              ({
                record,
                startIdx,
                endIdx,
                lane,
                continuesFromPrior,
                continuesToNext,
              }) => (
                <Box
                  key={record._id}
                  sx={{
                    gridColumn: `${startIdx + 2} / ${endIdx + 3}`,
                    gridRow: `${lane + 1}`,
                    zIndex: 1,
                    minWidth: 0,
                    mx: 0.75,
                    mt: 0.75,
                  }}
                >
                  <RosterAvailabilityBand
                    record={record}
                    continuesFromPrior={continuesFromPrior}
                    continuesToNext={continuesToNext}
                    visibleSpan={endIdx - startIdx + 1}
                  />
                </Box>
              ),
            )}

            {laidOutShifts.map(
              ({
                shift,
                startIdx,
                endIdx,
                lane,
                continuesFromPrior,
                continuesToNext,
              }) => (
                <Box
                  key={shift._id}
                  sx={{
                    gridColumn: `${startIdx + 2} / ${endIdx + 3}`,
                    gridRow: `${bandLaneCount + lane + 1}`,
                    zIndex: 1,
                    minWidth: 0,
                    m: 0.75,
                  }}
                >
                  <RosterShiftCard
                    shift={shift}
                    continuesFromPrior={continuesFromPrior}
                    continuesToNext={continuesToNext}
                    visibleSpan={endIdx - startIdx + 1}
                    duplicateCount={shift.duplicateCount}
                    pendingApplications={getShiftApplications(shift)}
                    onClick={() => openExistingShift(shift)}
                  />
                </Box>
              ),
            )}
          </Box>
          )
        })
      )}

      {showAvailableStaffRow && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: gridColumns(days, compact),
            minWidth: gridMinWidth(days, compact),
            borderTop: '2px solid',
            borderColor: 'divider',
            bgcolor: 'background.paper',
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
          }}
        >
          <Box
            sx={{
              p: 1,
              gridColumn: '1',
              borderRight: '1px solid',
              borderColor: 'divider',
              position: 'sticky',
              left: 0,
              zIndex: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" gap={0.75} alignItems="center">
              <EventAvailableIcon sx={{ fontSize: 18, color: 'success.main' }} />
              <Stack minWidth={0}>
                <Typography variant="subtitle2" noWrap>
                  Available staff
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  From My Availability
                </Typography>
              </Stack>
            </Stack>
          </Box>
          {days.map((day, dayIndex) => {
            const available = availableStaffByDay[day.format('YYYY-MM-DD')]
            return (
              <Box
                key={day.format('YYYY-MM-DD')}
                sx={{
                  gridColumn: `${dayIndex + 2}`,
                  p: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  ...(isToday(day) ? todayColumnSx : {}),
                }}
              >
                <Tooltip title={describeAvailableStaff(available)}>
                  <Chip
                    size="small"
                    icon={<EventAvailableIcon />}
                    label={
                      available?.count
                        ? `${available.count} available`
                        : 'None'
                    }
                    color={available?.count ? 'success' : 'default'}
                    variant={available?.count ? 'filled' : 'outlined'}
                    sx={{ borderRadius: 1, fontWeight: 700 }}
                  />
                </Tooltip>
              </Box>
            )
          })}
        </Box>
      )}
    </Paper>
  )
}

export default RosterGrid
